<template>
  <div class="header_wrapper">
    <header class="header">
      <div>
        <div v-if="!disableBackButton" class="header__back_button_container">
          <BackButton
            :to="backButtonTo"
            :label="backButtonLabel"
            :block="!(screenSizeIsTabletLike || screenSizeIsMobileLike)"
            class="header__back_button"
            :class="{
              'header__back_button--block': !(
                screenSizeIsTabletLike || screenSizeIsMobileLike
              ),
            }"
          />
          <hr class="back_button_container__divider" />
        </div>
        <hgroup
          class="header__content"
          :class="{ 'header__content--backbtn--enabled': !disableBackButton }"
        >
          <h1 class="header__title headline5">{{ title }}</h1>
          <slot name="subtitle">
            <h2 class="header__subtitle body1">{{ subtitle }}</h2>
          </slot>
        </hgroup>
        <div v-if="tabs" class="tabs">
          <button
            v-for="tab in tabs"
            :key="tab.name"
            class="tab"
            :class="{
              active: activeTab === tab.name,
              disabled: tab.disabled,
            }"
            @click="tab.disabled ? '' : onTabSelect(tab.name)"
            v-tippy
            :title="tab.tooltip"
            @show="() => !!tab.tooltip"
          >
            {{ tab.displayName }}
          </button>
        </div>
      </div>
      <div>
        <slot name="side-block"></slot>
      </div>
    </header>
  </div>
</template>

<script>
import { vueWindowSizeMixin } from "vue-window-size";
import { useBreakPoints } from "../../../util/useBreakPoints";
import BackButton from "../../atoms/BackButton.vue";

const { isBelow, isMd } = useBreakPoints();

export default {
  name: "SettingsHeader",
  mixins: [vueWindowSizeMixin],
  components: { BackButton },
  props: {
    /**
     * @vuese
     * Title becomes the main heading text. Required.
     */
    title: {
      type: String,
      required: true,
    },
    /**
     * Subtitle comes below the main heading.
     */
    subtitle: {
      type: String,
      required: false,
    },
    /**
     * The Vue router object including the 'to' property.
     * That will configure where the back button leads to.
     * Required if disableBackButton is set to true.
     */
    backButtonTo: {
      type: Object,
      required: false,
    },
    /**
     * The text that is shown in the back button during tablet and mobile-like screen sizes.
     * Required if disableBackButton is set to true.
     */
    backButtonLabel: {
      type: String,
      required: false,
    },
    /**
     * Setting this to true will hide the Backbutton.
     */
    disableBackButton: {
      type: Boolean,
      default: false,
    },
    /**
     * List of tabs to be display in header.
     */
    tabs: {
      type: Array,
      required: false,
    },
    /**
     * Method to be triggered when tab is selected.
     */
    onTabSelect: {
      type: Function,
      required: false,
    },
    /**
     * Name of currently selected tab.
     */
    activeTab: {
      type: String,
      required: false,
    },
  },
  computed: {
    screenSizeIsTabletLike() {
      return isMd(this.windowWidth);
    },
    screenSizeIsMobileLike() {
      return isBelow("md", this.windowWidth);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~include-media";
@import "../../../style_variables/style_variables.scss";

.header_wrapper {
  width: 100%;
  background-color: $white;
  border-bottom: 1px solid $grey_alabaster;
  padding: 0 24px;
  @include media($isPhone...) {
    padding: 0 16px;
  }
}

.header {
  margin: 0 auto;
  max-width: 950px;
  padding: 0 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @include media($isPhoneOrTablet...) {
    padding: 0;
    margin: 0;
  }
}

.header__back_button_container {
  height: 0;
  display: flex;
  align-items: center;

  @include media($isTablet...) {
    height: 54px;
    width: 100%;
  }
  @include media($isPhone...) {
    height: 54px;
    width: 100%;
    padding-left: 16px;
    flex-direction: column;
    justify-content: flex-end;
    align-items: flex-start;
  }

  .back_button_container__divider {
    @include media($isPhone) {
      background-color: $grey_alabaster;
      min-height: 1px;
      border: none;
      width: calc(100% + 48px);
      height: 1px;
      margin: 0;
      margin-left: -32px;
    }
  }
}

.header__back_button {
  height: 54px;
  display: flex;
  align-items: center;
}

.header__back_button--block {
  margin-top: 68px;
  position: relative;
  left: -60px;
  top: 28px;
}

.header__content {
  max-width: 900px;
  width: 100%;
  padding: 48px 24px 24px 0;

  @include media($isPhone...) {
    padding: 24px;
    &.header__content--backbtn--enabled {
      padding-top: 16px;
    }
  }
  @include media($isTablet...) {
    padding: 24px;
    &.header__content--backbtn--enabled {
      padding-top: 0;
    }
  }
}

.header__title {
  margin-bottom: 8px;
  margin-top: 0;
  font-weight: bold;
}

.header__subtitle {
  max-width: 556px;
  letter-spacing: 0;
  margin: 0;
}

.tabs {
  display: flex;
}

.tab {
  @extend %subtitle3_style;
  padding: 20px;
  background-color: $white;
  border: none;
  cursor: pointer;
  margin-right: 1px;

  &.active {
    color: $blue;
    border-bottom: 1px solid $blue;
  }
  &.disabled {
    color: $grey_french;
    cursor: default;
  }
}
</style>
